<template>
    <CardToggle :title="$t('shared.themes.ready_themes')">
        <div class="space-y-5">
            <FormSelect
                :tooltip="$t('shared.themes.style_themes_info')"
                :label="$t('shared.themes.style_themes')"
                :value="stylePreset"
                @change="setStylePreset($event)"
                :options="stylePresetsNew"
            />

            <NotificationInfo :close-button="false">
                <p>
                    {{ $t("shared.themes.settings1") }}
                    <a :href="brandingLink" @click.prevent="goToBranding" class="underline">
                        {{ $t("shared.themes.settings2") }}
                    </a>
                    {{ $t("shared.themes.settings3") }}
                </p>
            </NotificationInfo>

            <div class="relative space-y-2">
                <FormLabel :tooltip="$t('shared.themes.color_themes_info')">
                    <span>{{ $t("shared.themes.color_themes") }}</span>
                </FormLabel>

                <div
                    @click="toggleShowPresets"
                    class="ct-input"
                    :class="[showPresets ? 'ct-ring-focus !ring-2' : '']"
                >
                    <div class="cursor-pointer flex space-x-1 items-center w-full">
                        <p class="flex-grow">
                            {{ currentTheme.name }}
                        </p>
                        <c-theme-colors
                            v-if="currentTheme.colors"
                            :colors="[
                                currentTheme.colors.brand,
                                currentTheme.colors.background,
                                currentTheme.colors.cardBackground,
                            ]"
                        />
                        <ChevronDownIcon
                            :class="[showPresets ? 'rotate-180' : 'rotate-0']"
                            class="transition-transform flex-shrink-0 h-5 w-5 text-gray-400"
                        />
                    </div>

                    <div
                        v-click-outside="hidePresets"
                        v-if="showPresets"
                        class="w-full absolute -bottom-1 left-0 translate-y-full z-10"
                    >
                        <DropdownList class="w-full max-h-60 overflow-auto">
                            <DropdownItem
                                @click="setTheme(preset)"
                                v-for="preset in colorThemesWithoutUser"
                                :key="preset.id"
                                :is-selected="preset.id === currentTheme.id"
                            >
                                <p class="flex-grow text-left">{{ preset.name }}</p>
                                <c-theme-colors
                                    v-if="preset.colors"
                                    :colors="[
                                        preset.colors.brand,
                                        preset.colors.background,
                                        preset.colors.cardBackground,
                                    ]"
                                />
                            </DropdownItem>
                        </DropdownList>
                    </div>
                </div>
            </div>

            <ButtonSecondarySm
                v-if="currentTheme.id === 'user'"
                class="w-full"
                @click="showCreateThemeModal"
            >
                <AddIcon />
                <span>{{ $t("shared.themes.create_theme") }}</span>
            </ButtonSecondarySm>
        </div>
    </CardToggle>
    <ModalInput
        v-model:open="showThemeModal"
        v-model:text="newThemeName"
        :modal-data="createThemeModal"
        @cta="createTheme"
        @cancel="hideCreateThemeModal"
    />
</template>

<script>
import { CardToggle } from "@easy/ui/cards";
import { FormSelect, FormLabel } from "@easy/ui/forms";
import { AddIcon, ChevronDownIcon } from "@easy/ui/icons";
import { DropdownList, DropdownItem } from "@easy/ui/dropdowns";
import { ButtonSecondarySm } from "@easy/ui/buttons";
import { NotificationInfo } from "@easy/ui/notifications";
import { ModalInput } from "@easy/ui/modals";
import { mapActions, mapGetters } from "vuex";
import cThemeColors from "./cThemeColors.vue";
import { userService } from "@/shared/user/user.service";
import { userThemesService } from "@/shared/themes/user-themes.service";

export default {
    name: "cThemes",
    components: {
        CardToggle,
        FormSelect,
        FormLabel,
        AddIcon,
        ChevronDownIcon,
        cThemeColors,
        DropdownList,
        DropdownItem,
        ButtonSecondarySm,
        NotificationInfo,
        ModalInput,
    },
    props: {
        theme: String,
        styleTheme: String,
    },
    emits: ["updateColors", "updateStyle", "createTheme"],
    data() {
        return {
            showPresets: false,
            showThemeModal: false,
            newThemeName: "",
            createThemeModal: {
                title: this.$t("shared.themes.theme_name"),
                inputLabel: this.$t("shared.themes.theme_name_input"),
                ctaLabel: this.$t("shared.add"),
                cancelLabel: this.$t("shared.cancel"),
            },
        };
    },
    computed: {
        ...mapGetters("theme", ["colorThemes", "stylePresets"]),
        ...mapGetters(["brandingLink"]),
        stylePreset: {
            get() {
                return this.styleTheme;
            },
            set(value) {
                const preset = this.stylePresets.find((preset) => preset.id === value);
                this.$emit("updateStyle", preset);
            },
        },
        currentTheme() {
            return (
                this.colorThemes.find((preset) => preset.id === this.theme) ||
                this.colorThemes.find((preset) => preset.id === userThemesService.THEME_USER_ID)
            );
        },
        colorThemesWithoutUser() {
            return this.colorThemes.filter(
                (preset) => preset.id !== userThemesService.THEME_USER_ID
            );
        },
        stylePresetsNew() {
            return this.stylePresets.map((preset) => ({
                value: preset.id,
                label: preset.value,
            }));
        },
    },
    created() {
        this.getUserThemes();
    },
    methods: {
        ...mapActions("alert", ["showWarnMessage"]),
        ...mapActions("theme", ["getUserThemes"]),
        toggleShowPresets() {
            this.showPresets = !this.showPresets;
        },
        hidePresets() {
            this.showPresets = false;
        },
        setStylePreset(preset) {
            this.stylePreset = preset;
        },
        setTheme(theme) {
            this.$emit("updateColors", { theme });
            this.hidePresets();
        },
        showCreateThemeModal() {
            this.showThemeModal = true;
        },
        hideCreateThemeModal() {
            this.showThemeModal = false;
        },
        createTheme() {
            if (!this.newThemeName) {
                this.showWarnMessage({
                    message: this.$t("shared.themes.theme_name_input"),
                });
                return;
            }
            this.$emit("createTheme", this.newThemeName);
            this.showThemeModal = false;
        },
        async goToBranding() {
            const { magicUrl } = await userService.forgeMagicLink(this.brandingLink);
            window.open(magicUrl, "_blank");
        },
    },
};
</script>
